import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection, doc, getDoc, getDocs, query, setDoc, where, deleteField,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
  notifyAdminExamCompleted,
  notifyStudentExamCompleted,
} from '../utils/notifications';
import { EXAM_CONFIG } from '../utils/examSubjects';
import {
  activeRoundAttempts,
  activeRoundScores,
  cumulativeFromScores,
  listReadyExams,
  questionsForExam,
  shuffleList,
  tableCells,
} from '../utils/questionBank';
import { InlineMath, BlockMath } from 'react-katex';
import ProfileHeaderActions from '../components/ProfileHeaderActions';

/* ══════════════════════════════════════════
   Math-aware text renderer
══════════════════════════════════════════ */
function optionParts(opt) {
  if (opt && typeof opt === 'object') {
    return { text: opt.text || '', imageUrl: opt.imageUrl || '' };
  }
  return { text: String(opt || ''), imageUrl: '' };
}

function MathText({ text, style }) {
  if (!text) return null;
  const parts = text.split(/(\$\$[\s\S]*?\$\$|\$[^$\n]+\$)/g);
  return (
    <span style={style}>
      {parts.map((part, i) => {
        if (part.startsWith('$$') && part.endsWith('$$')) {
          try { return <BlockMath key={i} math={part.slice(2, -2).trim()} />; }
          catch { return <code key={i}>{part}</code>; }
        }
        if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
          try { return <InlineMath key={i} math={part.slice(1, -1).trim()} />; }
          catch { return <code key={i}>{part}</code>; }
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </span>
  );
}

/* ══════════════════════════════════════════
   Question type components
══════════════════════════════════════════ */

/* ── MCQ (also used for Diagram) ── */
function MCQAnswer({ q, answer, onChange, submitted, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {q.options.map((opt, i) => {
        const isSelected = answer === i;
        const isCorrect = submitted && i === q.correct;
        const isWrong = submitted && isSelected && i !== q.correct;
        let bg = '#fff', border = '#e2e8f0', textCol = '#334155';
        if (!submitted && isSelected) { bg = color + '10'; border = color; textCol = '#1b1b1d'; }
        if (isCorrect) { bg = '#f0fdf4'; border = '#16a34a'; textCol = '#15803d'; }
        if (isWrong) { bg = '#fef2f2'; border = '#ef4444'; textCol = '#b91c1c'; }

        return (
          <div key={i}>
            <button
              onClick={() => !submitted && onChange(i)}
              disabled={submitted}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                background: bg, border: `2px solid ${border}`, borderRadius: 12,
                padding: '13px 16px', textAlign: 'left', cursor: submitted ? 'default' : 'pointer',
                transition: 'all 0.15s', color: textCol, fontFamily: 'Plus Jakarta Sans, sans-serif',
              }}
            >
              <span style={{
                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0,
                background: isSelected || isCorrect ? color : '#e2e8f0',
                color: isSelected || isCorrect ? '#fff' : '#64748b',
              }}>{String.fromCharCode(65 + i)}</span>
              <span style={{ flex: 1, fontSize: 14, lineHeight: 1.5 }}>
                {optionParts(opt).imageUrl ? (
                  <img src={optionParts(opt).imageUrl} alt="" style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'contain', display: 'block', marginBottom: optionParts(opt).text ? 8 : 0 }} />
                ) : null}
                <MathText text={optionParts(opt).text} />
              </span>
              {isCorrect && <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#16a34a' }}>check_circle</span>}
              {isWrong && <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#ef4444' }}>cancel</span>}
            </button>
            {submitted && isCorrect && q.explanation && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, background: '#f0fdf4',
                border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginTop: 6,
                fontSize: 13, color: '#15803d', lineHeight: 1.5,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, flexShrink: 0 }}>lightbulb</span>
                <MathText text={q.explanation} />
              </div>
            )}
            {submitted && isWrong && q.explanation && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, background: '#fef2f2',
                border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginTop: 6,
                fontSize: 13, color: '#b91c1c', lineHeight: 1.5,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, flexShrink: 0 }}>lightbulb</span>
                <span><strong>Correct: </strong><MathText text={optionParts(q.options[q.correct]).text} /> — <MathText text={q.explanation} /></span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Matching / Drag-Drop ── */
function MatchingAnswer({ q, answer = {}, onChange, submitted, color }) {
  const [selected, setSelected] = useState(null);

  function clickRight(text) {
    if (submitted) return;
    setSelected((prev) => (prev === text ? null : text));
  }

  function clickLeft(idx) {
    if (submitted || selected === null) return;
    onChange({ ...answer, [idx]: selected });
    setSelected(null);
  }

  function clearLeft(idx, e) {
    e.stopPropagation();
    if (submitted) return;
    const next = { ...answer };
    delete next[idx];
    onChange(next);
  }

  const usedRights = new Set(Object.values(answer));
  const allCorrect = q.pairs.every((p, i) => answer[i] === p.right);

  return (
    <div>
      {selected && (
        <div style={{
          background: color + '10', border: `1px solid ${color}40`, borderRadius: 8,
          padding: '8px 14px', marginBottom: 12, fontSize: 13, color, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>touch_app</span>
          Selected: "{selected}" — now click a term on the left to match it
        </div>
      )}
      {!selected && !submitted && (
        <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 12 }}>
          Click an item on the right to select it, then click its matching term on the left.
        </p>
      )}

      <div className="sba-exam-match-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Left column — terms */}
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: 8 }}>TERMS</p>
          {q.pairs.map((pair, i) => {
            const assigned = answer[i];
            const isCorrect = submitted && assigned === pair.right;
            const isWrong = submitted && assigned && assigned !== pair.right;
            return (
              <div
                key={i}
                onClick={() => clickLeft(i)}
                style={{
                  border: `2px solid ${isCorrect ? '#16a34a' : isWrong ? '#ef4444' : selected !== null && !assigned ? color : '#e2e8f0'}`,
                  borderRadius: 10, padding: '12px 14px', marginBottom: 8, cursor: selected !== null && !submitted ? 'pointer' : 'default',
                  background: isCorrect ? '#f0fdf4' : isWrong ? '#fef2f2' : assigned ? color + '08' : '#fff',
                  transition: 'all 0.15s', minHeight: 62,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1b1b1d', marginBottom: assigned ? 6 : 0 }}>
                  <MathText text={pair.left} />
                </div>
                {assigned ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: isCorrect ? '#16a34a' : isWrong ? '#ef4444' : color, fontWeight: 600 }}>
                      → {assigned}
                    </span>
                    {!submitted && (
                      <button onClick={(e) => clearLeft(i, e)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16, padding: '0 2px', lineHeight: 1 }}>
                        ✕
                      </button>
                    )}
                  </div>
                ) : (
                  <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                    {selected ? 'Click to assign' : 'Unmatched'}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Right column — definitions (shuffled) */}
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: 8 }}>DEFINITIONS</p>
          {(q.displayRight || q.pairs.map((p) => p.right)).map((text, i) => {
            const isUsed = usedRights.has(text);
            const isSel = selected === text;
            return (
              <div
                key={i}
                onClick={() => clickRight(text)}
                style={{
                  border: `2px solid ${isSel ? color : isUsed ? '#d1d5db' : '#e2e8f0'}`,
                  borderRadius: 10, padding: '12px 14px', marginBottom: 8,
                  cursor: submitted || isUsed ? 'default' : 'pointer',
                  background: isSel ? color + '12' : isUsed ? '#f1f5f9' : '#fff',
                  opacity: isUsed && !isSel ? 0.55 : 1,
                  transition: 'all 0.15s', fontSize: 13, color: '#334155',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
              >
                <MathText text={text} />
                {isUsed && !submitted && <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 8 }}>✓ used</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Answer key after submit */}
      {submitted && (
        <div style={{ marginTop: 14, padding: '14px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>Correct Matches:</p>
          {q.pairs.map((p, i) => (
            <div key={i} style={{ fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontWeight: 600 }}>{p.left}</span>
              <span className="material-symbols-outlined" style={{ fontSize: 14, color: '#10b981' }}>arrow_forward</span>
              <span>{p.right}</span>
            </div>
          ))}
          {q.explanation && (
            <p style={{ fontSize: 12, color: '#64748b', marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>{q.explanation}</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Fill in the Blank ── */
function FillAnswer({ q, answer, onChange, submitted }) {
  const isCorrect = submitted && answer?.trim().toLowerCase() === q.answer?.toLowerCase();
  const isWrong = submitted && !isCorrect && answer?.trim();
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input
          type="text"
          value={answer || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={submitted}
          placeholder="Type your answer here…"
          style={{
            padding: '13px 16px', fontSize: 16, fontFamily: 'Plus Jakarta Sans, sans-serif',
            border: `2px solid ${submitted ? (isCorrect ? '#16a34a' : isWrong ? '#ef4444' : '#e2e8f0') : '#e2e8f0'}`,
            borderRadius: 12, background: submitted ? (isCorrect ? '#f0fdf4' : isWrong ? '#fef2f2' : '#fff') : '#fff',
            color: '#1b1b1d', outline: 'none', minWidth: 220, flex: 1, maxWidth: 400,
          }}
        />
        {submitted && (
          <span style={{ fontWeight: 700, fontSize: 14, color: isCorrect ? '#16a34a' : '#ef4444', display: 'flex', alignItems: 'center', gap: 5 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{isCorrect ? 'check_circle' : 'cancel'}</span>
            {isCorrect ? 'Correct!' : `Answer: ${q.answer}`}
          </span>
        )}
      </div>
      {submitted && q.hint && (
        <div style={{ marginTop: 10, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 13, color: '#92400e', display: 'flex', gap: 8 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>lightbulb</span>
          <MathText text={q.hint} />
        </div>
      )}
    </div>
  );
}

/* ── Coordinate Grid (Hotspot) ── */
function CoordinateAnswer({ q, answer, onChange, submitted, color }) {
  const SIZE = 280;
  const range = q.gridRange || 5;
  const STEP = SIZE / (range * 2);
  const C = SIZE / 2;

  function toSvgX(val) { return C + val * STEP; }
  function toSvgY(val) { return C - val * STEP; }

  function handleClick(e) {
    if (submitted) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const rawX = e.clientX - rect.left;
    const rawY = e.clientY - rect.top;
    const x = Math.round((rawX - C) / STEP);
    const y = Math.round((C - rawY) / STEP);
    if (Math.abs(x) <= range && Math.abs(y) <= range) {
      onChange({ x, y });
    }
  }

  const isCorrect = submitted && answer?.x === q.correctX && answer?.y === q.correctY;

  const gridNums = Array.from({ length: range * 2 + 1 }, (_, i) => i - range);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
      <svg
        width={SIZE}
        height={SIZE}
        onClick={handleClick}
        style={{
          cursor: submitted ? 'default' : 'crosshair',
          border: '1.5px solid #e2e8f0',
          borderRadius: 14,
          background: '#fafcff',
          userSelect: 'none',
        }}
      >
        {/* Grid lines */}
        {gridNums.map((v) => (
          <g key={v}>
            <line x1={toSvgX(v)} y1={0} x2={toSvgX(v)} y2={SIZE}
              stroke={v === 0 ? '#334155' : '#e2e8f0'} strokeWidth={v === 0 ? 1.5 : 0.8} />
            <line x1={0} y1={toSvgY(v)} x2={SIZE} y2={toSvgY(v)}
              stroke={v === 0 ? '#334155' : '#e2e8f0'} strokeWidth={v === 0 ? 1.5 : 0.8} />
            {v !== 0 && (
              <>
                <text x={toSvgX(v)} y={C + 14} textAnchor="middle" fontSize={8} fill="#94a3b8">{v}</text>
                <text x={C - 8} y={toSvgY(v) + 3} textAnchor="end" fontSize={8} fill="#94a3b8">{v}</text>
              </>
            )}
          </g>
        ))}

        {/* Axis labels */}
        <text x={SIZE - 5} y={C + 12} textAnchor="end" fontSize={10} fill="#334155" fontWeight="bold">x</text>
        <text x={C + 8} y={10} textAnchor="start" fontSize={10} fill="#334155" fontWeight="bold">y</text>

        {/* Correct point (after submit) */}
        {submitted && (
          <>
            <circle cx={toSvgX(q.correctX)} cy={toSvgY(q.correctY)} r={12}
              fill="none" stroke="#16a34a" strokeWidth={2} strokeDasharray="4 2" />
            <circle cx={toSvgX(q.correctX)} cy={toSvgY(q.correctY)} r={4} fill="#16a34a" />
          </>
        )}

        {/* Student's point */}
        {answer && (
          <>
            <circle cx={toSvgX(answer.x)} cy={toSvgY(answer.y)} r={7}
              fill={submitted ? (isCorrect ? '#16a34a' : '#ef4444') : color}
              stroke="white" strokeWidth={2} />
            <text x={toSvgX(answer.x) + 11} y={toSvgY(answer.y) - 6}
              fontSize={10} fill={submitted ? (isCorrect ? '#16a34a' : '#ef4444') : color} fontWeight="bold">
              ({answer.x}, {answer.y})
            </text>
          </>
        )}
      </svg>

      {!submitted && (
        <p style={{ fontSize: 13, color: '#64748b', textAlign: 'center' }}>
          {answer ? `Plotted: (${answer.x}, ${answer.y}) — click anywhere to replot` : 'Click on the grid to plot your point'}
        </p>
      )}
      {submitted && (
        <div style={{
          padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600, textAlign: 'center',
          background: isCorrect ? '#f0fdf4' : '#fef2f2',
          color: isCorrect ? '#15803d' : '#b91c1c',
          border: `1px solid ${isCorrect ? '#bbf7d0' : '#fecaca'}`,
        }}>
          {isCorrect
            ? `✓ Correct! (${q.correctX}, ${q.correctY})`
            : `✗ Correct point: (${q.correctX}, ${q.correctY}). You plotted: (${answer?.x ?? '?'}, ${answer?.y ?? '?'})`}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   Loading Screen
══════════════════════════════════════════ */
function LoadingScreen({ config }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 20, padding: 32 }}>
      <div style={{ width: 72, height: 72, borderRadius: 20, background: config.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="material-symbols-outlined" style={{ fontSize: 40, color: config.color }}>quiz</span>
      </div>
      <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 22, fontWeight: 800, color: '#1b1b1d' }}>
        Preparing your exam…
      </h2>
      <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', maxWidth: 380 }}>
        AI is generating 25 GED-level questions for <strong>{config.label}</strong>. This takes about 30 seconds.
      </p>
      <div style={{ width: 240, height: 5, background: '#e2e8f0', borderRadius: 9999, overflow: 'hidden' }}>
        <div style={{ height: '100%', background: config.color, borderRadius: 9999, animation: 'gedSlide 2s ease-in-out infinite' }} />
      </div>
      <style>{`@keyframes gedSlide { 0%{width:0%;margin-left:0} 50%{width:80%;margin-left:10%} 100%{width:0%;margin-left:100%} }`}</style>
    </div>
  );
}

/* ══════════════════════════════════════════
   Results / Review Screen
══════════════════════════════════════════ */
function ResultsScreen({ config, score, questions, answers, phase, locked, roundMessage, onReview, onRetake, onBack, onCloseReview }) {
  const passed = score.percentage >= 70;
  const [reviewQ, setReviewQ] = useState(0);

  if (phase === 'review') {
    const q = questions[reviewQ];
    return (
      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', background: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Review topbar */}
        <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={onCloseReview || onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_back</span>Back to report
          </button>
          <div style={{ flex: 1, textAlign: 'center', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 15, fontWeight: 800, color: '#1b1b1d' }}>
            Review: {config.label}
          </div>
          <span style={{ fontSize: 13, color: '#64748b' }}>Q {reviewQ + 1} / {questions.length}</span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', gap: 0 }}>
          {/* Q navigator */}
          <div style={{ width: 180, flexShrink: 0, background: '#fff', borderRight: '1px solid #e2e8f0', padding: '16px 10px', overflowY: 'auto' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {questions.map((q, i) => {
                let ans = answers[i];
                let correct = false;
                if (q.type === 'mcq' || q.type === 'diagram') correct = ans === q.correct;
                else if (q.type === 'matching') correct = q.pairs.every((p, j) => ans?.[j] === p.right);
                else if (q.type === 'fill') correct = ans?.trim().toLowerCase() === q.answer?.toLowerCase();
                else if (q.type === 'coordinate') correct = ans?.x === q.correctX && ans?.y === q.correctY;
                return (
                  <button key={i} onClick={() => setReviewQ(i)} style={{
                    width: 34, height: 34, borderRadius: 8, border: `2px solid ${i === reviewQ ? config.color : correct ? '#16a34a' : '#ef4444'}`,
                    background: i === reviewQ ? config.color : correct ? '#f0fdf4' : '#fef2f2',
                    color: i === reviewQ ? '#fff' : correct ? '#16a34a' : '#ef4444',
                    fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  }}>{i + 1}</button>
                );
              })}
            </div>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[['#16a34a', 'Correct'], ['#ef4444', 'Incorrect']].map(([c, l]) => (
                <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: c }} />{l}
                </div>
              ))}
            </div>
          </div>

          {/* Review question */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
            <div style={{ maxWidth: 680, margin: '0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ background: config.color + '15', color: config.color, borderRadius: 20, padding: '3px 12px', fontSize: 11, fontWeight: 700 }}>
                  {q.type === 'mcq' ? 'Multiple Choice' : q.type === 'matching' ? 'Matching' : q.type === 'fill' ? 'Fill in the Blank' : q.type === 'diagram' ? 'Data Analysis' : 'Coordinate Grid'}
                </span>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>Question {reviewQ + 1}</span>
              </div>

              {q.type === 'diagram' && (
                <div style={{ background: '#f0f9ff', border: '1.5px solid #bae6fd', borderRadius: 12, padding: '16px', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#0369a1' }}>bar_chart</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#0369a1' }}>FIGURE / DATA</span>
                  </div>
                  <p style={{ fontSize: 14, color: '#0c4a6e', lineHeight: 1.6, margin: 0 }}>{q.diagramText}</p>
                </div>
              )}

              <ExamExhibit exhibit={q.exhibit} />
              <p style={{ fontSize: 17, fontWeight: 600, color: '#1b1b1d', lineHeight: 1.6, marginBottom: 20 }}>
                <MathText text={q.question || q.instruction || ''} />
              </p>

              {(q.type === 'mcq' || q.type === 'diagram') && (
                <MCQAnswer q={q} answer={answers[reviewQ]} onChange={() => {}} submitted color={config.color} />
              )}
              {q.type === 'matching' && (
                <MatchingAnswer q={q} answer={answers[reviewQ] || {}} onChange={() => {}} submitted color={config.color} />
              )}
              {q.type === 'fill' && (
                <FillAnswer q={q} answer={answers[reviewQ] || ''} onChange={() => {}} submitted />
              )}
              {q.type === 'coordinate' && (
                <CoordinateAnswer q={q} answer={answers[reviewQ] || null} onChange={() => {}} submitted color={config.color} />
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
                <button onClick={() => setReviewQ((c) => Math.max(c - 1, 0))} disabled={reviewQ === 0}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: reviewQ > 0 ? 'pointer' : 'not-allowed', color: '#64748b', opacity: reviewQ > 0 ? 1 : 0.4 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_back</span>Previous
                </button>
                <button onClick={() => setReviewQ((c) => Math.min(c + 1, questions.length - 1))} disabled={reviewQ === questions.length - 1}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: config.color, border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: reviewQ < questions.length - 1 ? 'pointer' : 'not-allowed', color: '#fff', opacity: reviewQ < questions.length - 1 ? 1 : 0.4 }}>
                  Next<span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_forward</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* Results summary screen */
  return (
    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', background: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 24, padding: '40px 48px', maxWidth: 560, width: '100%', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.08)', border: '1.5px solid #e2e8f0' }}>
        <div style={{ width: 80, height: 80, borderRadius: 22, background: (passed ? '#f0fdf4' : '#fff7ed'), display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 44, color: passed ? '#16a34a' : '#f59e0b' }}>
            {passed ? 'emoji_events' : 'school'}
          </span>
        </div>
        <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 26, fontWeight: 800, color: '#1b1b1d', marginBottom: 8 }}>
          {passed ? 'Congratulations! 🎉' : 'Keep Studying!'}
        </h2>
        <p style={{ fontSize: 14, color: '#64748b', marginBottom: 28, lineHeight: 1.5 }}>
          {roundMessage === 'ready'
            ? 'You are ready to appear in the exam. Contact the Advisor. All exams are open again.'
            : roundMessage === 'practice'
              ? 'You need more practice. All exams are open again.'
              : locked
                ? 'This exam is locked until every exam is finished. You can review your answers and this report.'
                : passed
                  ? `You passed the ${config.label} practice exam.`
                  : 'You did not reach 70% this time.'}
        </p>

        {/* Score ring */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 32, marginBottom: 28 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 52, fontWeight: 900, color: passed ? '#16a34a' : '#f59e0b', lineHeight: 1 }}>
              {score.percentage}%
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Score</div>
          </div>
          <div style={{ width: 1, height: 60, background: '#e2e8f0' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 36, fontWeight: 900, color: '#1b1b1d', lineHeight: 1 }}>
              {score.correct}<span style={{ fontSize: 20, color: '#94a3b8' }}>/{score.total}</span>
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Correct</div>
          </div>
          <div style={{ width: 1, height: 60, background: '#e2e8f0' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 28, fontWeight: 800, color: passed ? '#16a34a' : '#ef4444', lineHeight: 1 }}>
              {passed ? 'PASS' : 'FAIL'}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Result</div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ height: 10, background: '#e2e8f0', borderRadius: 9999, overflow: 'hidden', marginBottom: 28 }}>
          <div style={{ height: '100%', width: `${score.percentage}%`, background: passed ? '#16a34a' : '#f59e0b', borderRadius: 9999 }} />
        </div>
        <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 28 }}>Passing score: 70% ({Math.ceil((score?.total || 0) * 0.7)}/{score?.total || 0} correct)</p>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={onReview} disabled={!questions.length} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '13px 18px', fontSize: 13, fontWeight: 700, cursor: questions.length ? 'pointer' : 'not-allowed', color: '#334155', opacity: questions.length ? 1 : 0.5 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 17 }}>manage_search</span>
            Review Answers
          </button>
          {!locked && (
            <button onClick={onRetake} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: config.color, border: 'none', borderRadius: 12, padding: '13px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#fff' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 17 }}>replay</span>
              Retake Exam
            </button>
          )}
          <button onClick={onBack} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '11px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#64748b' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 17 }}>grid_view</span>
            Back to All Exams
          </button>
        </div>
      </div>
    </div>
  );
}

function ExamExhibit({ exhibit = [] }) {
  if (!exhibit.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
      {exhibit.map((block, index) => {
        if (block.type === 'image' && block.url) {
          return <img key={index} src={block.url} alt="" style={{ maxWidth: '100%', borderRadius: 12, border: '1px solid #e2e8f0' }} />;
        }
        if (block.type === 'table') {
          return (
            <div key={index} style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                <tbody>
                  {(block.rows || []).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {tableCells(row).map((cell, cellIndex) => (
                        <td key={cellIndex} style={{ border: '1px solid #e2e8f0', padding: '6px 8px' }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.type === 'text' && block.text) {
          return <p key={index} style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: '#334155' }}><MathText text={block.text} /></p>;
        }
        return null;
      })}
    </div>
  );
}

function toPlayerQuestion(id, data) {
  return {
    id,
    type: 'mcq',
    question: data.stem || '',
    exhibit: data.exhibit || [],
    options: data.options || [],
    correct: Number.isFinite(data.correct) ? data.correct : 0,
    explanation: data.explanation || '',
  };
}

/* ══════════════════════════════════════════
   Main ExamPage component
══════════════════════════════════════════ */
export default function ExamPage() {
  const { subject, examId } = useParams();
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const config = EXAM_CONFIG[subject];

  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState('loading'); // loading | active | submitted | review
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [examTitle, setExamTitle] = useState('');
  const [score, setScore] = useState(null);
  const [attemptLocked, setAttemptLocked] = useState(false);
  const [roundMessage, setRoundMessage] = useState('');
  const submittedRef = useRef(false);
  const timerRef = useRef(null);
  const bankRef = useRef([]);

  function startAttempt(list, examMinutes) {
    submittedRef.current = false;
    setRoundMessage('');
    setAttemptLocked(false);
    setQuestions(shuffleList(list));
    setAnswers({});
    setCurrentQ(0);
    setScore(null);
    setTimeLeft(examMinutes * 60);
    setPhase('active');
  }

  /* Load the dealt questions for this exam */
  useEffect(() => {
    if (!subject || !config || !examId) { navigate('/exams'); return; }
    let cancelled = false;
    (async () => {
      try {
        const examSnap = await getDoc(doc(db, 'practiceExams', examId));
        if (!examSnap.exists() || examSnap.data().subject !== subject) {
          throw new Error('This exam is not available.');
        }
        const exam = { id: examSnap.id, ...examSnap.data() };
        const [examListSnap, bankSnap, attemptSnap] = await Promise.all([
          getDocs(query(collection(db, 'practiceExams'), where('subject', '==', subject))),
          getDocs(collection(db, 'questionBanks', subject, 'questions')),
          currentUser ? getDoc(doc(db, 'examAttempts', currentUser.uid)) : Promise.resolve(null),
        ]);
        const subjectExams = examListSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
        const bankIds = bankSnap.docs.map((item) => item.id);
        const attemptData = attemptSnap?.exists() ? attemptSnap.data() : {};
        const scores = activeRoundScores(attemptData);
        const attemptsMap = activeRoundAttempts(attemptData);
        let ids = exam.questionIds || [];
        if (!ids.length) ids = questionsForExam(exam, subjectExams, bankIds);
        const savedAttempt = attemptsMap[examId] || null;
        const savedScore = scores[examId] || null;
        const staysLocked = Boolean(savedScore) && !isAdmin;
        const alreadySubmitted = staysLocked;
        const order = savedAttempt?.questionOrder?.length ? savedAttempt.questionOrder : ids;
        if (!order.length && !alreadySubmitted) {
          throw new Error('This exam has no questions yet. Add questions to the subject bank first.');
        }
        const snaps = order.length
          ? await Promise.all(order.map((id) => getDoc(doc(db, 'questionBanks', subject, 'questions', id))))
          : [];
        const loaded = snaps.filter((snap) => snap.exists()).map((snap) => toPlayerQuestion(snap.id, snap.data()));
        if (!loaded.length && !alreadySubmitted) throw new Error('This exam is not ready yet.');
        if (cancelled) return;
        const examMinutes = Math.max(1, Number(exam.timeMinutes) || 60);
        bankRef.current = loaded;
        setMinutes(examMinutes);
        setExamTitle(savedAttempt?.examTitle || savedScore?.examTitle || exam.title || config.label);
        if (alreadySubmitted) {
          const storedAnswers = savedAttempt?.questionOrder?.length ? (savedAttempt.answers || {}) : {};
          const restored = {};
          Object.entries(storedAnswers).forEach(([key, value]) => { restored[Number(key)] = value; });
          setQuestions(savedAttempt?.questionOrder?.length ? loaded : []);
          setAnswers(restored);
          const source = savedAttempt || savedScore;
          setScore({
            correct: source.correct,
            total: source.total,
            percentage: source.percentage,
          });
          setAttemptLocked(staysLocked);
          submittedRef.current = true;
          setPhase('submitted');
          return;
        }
        setAttemptLocked(false);
        startAttempt(loaded, examMinutes);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Could not open this exam.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [subject, examId, config, navigate, currentUser, isAdmin]);

  /* Timer */
  useEffect(() => {
    if (phase !== 'active') return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]); // eslint-disable-line

  function formatTime(secs) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  function calcScore(qs, ans) {
    let correct = 0;
    qs.forEach((q, i) => {
      const a = ans[i];
      if (q.type === 'mcq' || q.type === 'diagram') { if (a === q.correct) correct++; }
      else if (q.type === 'matching') { if (q.pairs.every((p, j) => a?.[j] === p.right)) correct++; }
      else if (q.type === 'fill') { if (a?.trim().toLowerCase() === q.answer?.toLowerCase()) correct++; }
      else if (q.type === 'coordinate') { if (a?.x === q.correctX && a?.y === q.correctY) correct++; }
    });
    return { correct, total: qs.length, percentage: Math.round((correct / qs.length) * 100) };
  }

  function handleSubmit() {
    if (submittedRef.current) return;
    submittedRef.current = true;
    clearInterval(timerRef.current);
    const result = calcScore(questions, answers);
    setScore(result);
    const summary = {
      subject,
      examTitle: examTitle || config?.label || subject,
      correct: result.correct,
      total: result.total,
      percentage: result.percentage,
      passed: result.percentage >= 70,
      attemptedAt: new Date().toISOString(),
    };
    const storedAnswers = {};
    Object.entries(answers).forEach(([key, value]) => {
      if (value !== undefined) storedAnswers[String(key)] = value;
    });
    const attemptPayload = {
      ...summary,
      questionOrder: questions.map((question) => question.id).filter(Boolean),
      answers: storedAnswers,
    };
    setAttemptLocked(!isAdmin);
    setRoundMessage('');
    setPhase('submitted');
    if (!currentUser) {
      setAttemptLocked(false);
      return;
    }
    const attemptRef = doc(db, 'examAttempts', currentUser.uid);
    (async () => {
      const snap = await getDoc(attemptRef);
      const data = snap.exists() ? snap.data() : {};
      const nextScores = { ...activeRoundScores(data), [examId]: summary };
      const nextAttempts = { ...activeRoundAttempts(data), [examId]: attemptPayload };
      const examSnap = await getDocs(collection(db, 'practiceExams'));
      const exams = examSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
      const subjects = [...new Set(exams.map((item) => item.subject).filter(Boolean))];
      const bankPairs = await Promise.all(subjects.map(async (subjectId) => {
        const bankSnap = await getDocs(collection(db, 'questionBanks', subjectId, 'questions'));
        return [subjectId, bankSnap.docs.map((item) => item.id)];
      }));
      const report = !isAdmin
        ? cumulativeFromScores(listReadyExams(exams, Object.fromEntries(bankPairs)), nextScores)
        : null;
      if (report) {
        await setDoc(attemptRef, {
          lastReport: report,
          activeScores: deleteField(),
          activeAttempts: deleteField(),
          bestScores: deleteField(),
          attempts: deleteField(),
          roundCleared: true,
        }, { merge: true });
        setAttemptLocked(false);
        setRoundMessage(report.ready ? 'ready' : 'practice');
      } else {
        await setDoc(attemptRef, {
          activeScores: nextScores,
          activeAttempts: nextAttempts,
          roundCleared: false,
        }, { merge: true });
        setAttemptLocked(!isAdmin);
      }
      notifyStudentExamCompleted({
        userId: currentUser.uid,
        subject: examTitle || config?.label || subject,
        score: result.percentage,
      }).catch(console.warn);
      notifyAdminExamCompleted({
        studentName: currentUser.displayName,
        email: currentUser.email,
        subject: examTitle || config?.label || subject,
        score: result.percentage,
      }).catch(console.warn);
    })().catch(console.warn);
  }

  function setAnswer(idx, val) {
    setAnswers((prev) => ({ ...prev, [idx]: val }));
  }

  function isAnswered(i) {
    const a = answers[i];
    const q = questions[i];
    if (!q) return false;
    if (q.type === 'matching') return q.pairs.every((_, j) => a?.[j] !== undefined);
    if (q.type === 'fill') return a?.trim().length > 0;
    return a !== undefined && a !== null && a !== '';
  }

  const answeredCount = questions.filter((_, i) => isAnswered(i)).length;
  const canSubmit = answeredCount === questions.length;

  if (!config) return null;
  if (phase === 'loading') return <LoadingScreen config={config} />;

  if (phase === 'error') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 16, padding: 32, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        <span className="material-symbols-outlined" style={{ fontSize: 52, color: '#ef4444' }}>error</span>
        <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 20, fontWeight: 800, color: '#1b1b1d' }}>Could not load exam</h2>
        <p style={{ color: '#64748b', fontSize: 14, textAlign: 'center', maxWidth: 360 }}>{error}</p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={() => { setPhase('loading'); setError(null); }} style={{ background: config.color, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Retry
          </button>
          <button onClick={() => navigate('/exams')} style={{ background: '#fff', color: '#334155', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '12px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Back to Exams
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'submitted' || phase === 'review') {
    return (
      <ResultsScreen
        config={{ ...config, label: examTitle || config.label }}
        score={score}
        questions={questions}
        answers={answers}
        phase={phase}
        locked={attemptLocked}
        roundMessage={roundMessage}
        onReview={() => setPhase('review')}
        onRetake={() => startAttempt(bankRef.current, minutes)}
        onBack={() => navigate('/exams')}
        onCloseReview={() => setPhase('submitted')}
      />
    );
  }

  const q = questions[currentQ];
  if (!q) return null;
  const typeLabel = { mcq: 'Multiple Choice', matching: 'Matching / Drag-Drop', fill: 'Fill in the Blank', diagram: 'Data Analysis', coordinate: 'Coordinate Grid' }[q.type] || q.type;

  /* ── Active exam layout ── */
  return (
    <div style={ex.page}>
      <style>{`@keyframes gedBlink{0%,100%{opacity:1}50%{opacity:0}}`}</style>

      {/* ── Top bar ── */}
      <div style={ex.topBar}>
        <button onClick={() => navigate('/exams')} style={ex.exitBtn} title="Exit exam">
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
        </button>
        <div style={ex.topCenter}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: config.color, flexShrink: 0 }} />
          <span style={ex.topSubject}>{examTitle || config.label}</span>
        </div>
        <div style={ex.topRight}>
          <ProfileHeaderActions compact />
          <div style={ex.timerChip(timeLeft < 300)}>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>timer</span>
            {formatTime(timeLeft)}
          </div>
          <span style={ex.answeredChip}>{answeredCount}/{questions.length}</span>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{ ...ex.submitTopBtn, background: canSubmit ? config.color : '#e2e8f0', color: canSubmit ? '#fff' : '#94a3b8', cursor: canSubmit ? 'pointer' : 'default' }}
          >
            Submit
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={ex.body}>
        {/* ── Left: Q navigator ── */}
        <div style={ex.navigator}>
          <p style={ex.navTitle}>Questions</p>
          <div style={ex.navGrid}>
            {questions.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentQ(i)}
                style={{
                  width: 34, height: 34, borderRadius: 8, border: `2px solid ${i === currentQ ? config.color : isAnswered(i) ? '#10b981' : '#e2e8f0'}`,
                  background: i === currentQ ? config.color : isAnswered(i) ? '#f0fdf4' : '#fff',
                  color: i === currentQ ? '#fff' : isAnswered(i) ? '#16a34a' : '#64748b',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer',
                }}
              >{i + 1}</button>
            ))}
          </div>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 5 }}>
            {[
              [config.color, 'Current'],
              ['#10b981', 'Answered'],
              ['#e2e8f0', 'Unanswered'],
            ].map(([c, l]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}>
                <div style={{ width: 11, height: 11, borderRadius: 3, background: c, border: '1px solid #e2e8f0' }} />
                {l}
              </div>
            ))}
          </div>
        </div>

        {/* ── Right: Question panel ── */}
        <div style={ex.questionPanel}>
          <div style={ex.qHeader}>
            <span style={{ ...ex.typePill, background: config.color + '15', color: config.color }}>{typeLabel}</span>
            <span style={ex.qNum}>Question {currentQ + 1} of {questions.length}</span>
          </div>

          {/* Diagram box */}
          {q.type === 'diagram' && q.diagramText && (
            <div style={ex.diagramBox}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#0369a1' }}>
                  {q.diagramType === 'bar_chart' ? 'bar_chart' : q.diagramType === 'line_chart' ? 'show_chart' : q.diagramType === 'table' ? 'table_chart' : 'article'}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', letterSpacing: '0.05em' }}>
                  {q.diagramType?.replace('_', ' ').toUpperCase() || 'FIGURE'}
                </span>
              </div>
              <p style={{ fontSize: 14, color: '#0c4a6e', lineHeight: 1.7, margin: 0 }}>{q.diagramText}</p>
            </div>
          )}

          <ExamExhibit exhibit={q.exhibit} />

          {/* Question text */}
          <div style={ex.questionText}>
            <MathText text={q.question || q.instruction || ''} style={{ fontSize: 17, fontWeight: 600, color: '#1b1b1d', lineHeight: 1.6 }} />
          </div>

          {/* Answer area */}
          <div style={{ marginBottom: 24 }}>
            {(q.type === 'mcq' || q.type === 'diagram') && (
              <MCQAnswer q={q} answer={answers[currentQ]} onChange={(v) => setAnswer(currentQ, v)} submitted={false} color={config.color} />
            )}
            {q.type === 'matching' && (
              <MatchingAnswer q={q} answer={answers[currentQ] || {}} onChange={(v) => setAnswer(currentQ, v)} submitted={false} color={config.color} />
            )}
            {q.type === 'fill' && (
              <FillAnswer q={q} answer={answers[currentQ] || ''} onChange={(v) => setAnswer(currentQ, v)} submitted={false} />
            )}
            {q.type === 'coordinate' && (
              <CoordinateAnswer q={q} answer={answers[currentQ] || null} onChange={(v) => setAnswer(currentQ, v)} submitted={false} color={config.color} />
            )}
          </div>

          {/* Prev / Next */}
          <div style={ex.navRow}>
            <button
              onClick={() => setCurrentQ((c) => Math.max(c - 1, 0))}
              disabled={currentQ === 0}
              style={{ ...ex.navBtn(false), opacity: currentQ > 0 ? 1 : 0.35 }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_back</span>
              Previous
            </button>
            {currentQ < questions.length - 1 ? (
              <button onClick={() => setCurrentQ((c) => c + 1)} style={ex.navBtn(true, config.color)}>
                Next
                <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_forward</span>
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                style={{ ...ex.navBtn(true, config.color), background: canSubmit ? config.color : '#e2e8f0', color: canSubmit ? '#fff' : '#94a3b8', cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: 1 }}
              >
                Submit Exam
                <span className="material-symbols-outlined" style={{ fontSize: 17 }}>send</span>
              </button>
            )}
          </div>

          {!canSubmit && currentQ === questions.length - 1 && (
            <p style={{ fontSize: 12, color: '#f59e0b', textAlign: 'center', marginTop: 8 }}>
              Answer all {questions.length} questions before submitting ({questions.length - answeredCount} remaining)
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Styles ── */
const ex = {
  page: { display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', fontFamily: 'Plus Jakarta Sans, sans-serif', background: '#f8fafc' },
  topBar: { display: 'flex', alignItems: 'center', gap: 16, background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '10px 20px', flexShrink: 0 },
  exitBtn: { background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', padding: 6 },
  topCenter: { flex: 1, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' },
  topSubject: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 14, fontWeight: 800, color: '#1b1b1d' },
  topRight: { display: 'flex', alignItems: 'center', gap: 10 },
  timerChip: (urgent) => ({
    display: 'flex', alignItems: 'center', gap: 5, background: urgent ? '#fef2f2' : '#f8fafc',
    border: `1px solid ${urgent ? '#fecaca' : '#e2e8f0'}`, borderRadius: 20, padding: '5px 12px',
    fontSize: 13, fontWeight: 700, color: urgent ? '#ef4444' : '#334155',
    animation: urgent ? 'gedBlink 1s infinite' : 'none',
  }),
  answeredChip: { fontSize: 12, fontWeight: 600, color: '#94a3b8' },
  submitTopBtn: { border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700 },
  body: { flex: 1, display: 'flex', overflow: 'hidden' },
  navigator: { width: 160, flexShrink: 0, background: '#fff', borderRight: '1px solid #e2e8f0', padding: '16px 12px', overflowY: 'auto' },
  navTitle: { fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.07em', marginBottom: 12 },
  navGrid: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  questionPanel: { flex: 1, overflowY: 'auto', padding: '28px 36px 80px' },
  qHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 },
  typePill: { borderRadius: 20, padding: '4px 13px', fontSize: 11, fontWeight: 700 },
  qNum: { fontSize: 12, color: '#94a3b8', marginLeft: 'auto' },
  diagramBox: { background: '#f0f9ff', border: '1.5px solid #bae6fd', borderRadius: 14, padding: '16px 20px', marginBottom: 20 },
  questionText: { marginBottom: 24, maxWidth: 680 },
  navRow: { display: 'flex', justifyContent: 'space-between', gap: 12, paddingTop: 24, borderTop: '1px solid #f1f5f9' },
  navBtn: (isPrimary, color) => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', borderRadius: 12,
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
    background: isPrimary ? (color || '#6332ed') : '#fff',
    color: isPrimary ? '#fff' : '#334155',
    border: isPrimary ? 'none' : '1.5px solid #e2e8f0',
    boxShadow: isPrimary ? `0 4px 14px ${color || '#6332ed'}30` : 'none',
  }),
};
